import type { QuickSearchCategoryName, QuickSearchCategoryPage, QuickSearchLanguage } from '@/types/searchTypes'
import { getCurrentLanguage } from '@offer-catalog/services/i18n'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getQuickSearch, getQuickSearchCategory } from '@/api'
import { useInfiniteScrollQuery } from '@/hooks/useInfiniteScrollQuery'
import { queryKeys } from '@/query/queryKeys'
import { isValidQuickSearchQuery } from '@/utils/quickSearch'

const QUICK_SEARCH_PAGE_SIZE = 20

interface QuickSearchPageParam {
  page: number
  pageSize: number
}

interface UseQuickSearchOptions {
  keepPreviousData?: boolean
  enabled?: boolean
}

export function useQuickSearch(query: string, season: string, options: UseQuickSearchOptions = {}) {
  const normalizedQuery = query.trim()
  const lang = getCurrentLanguage().toUpperCase() as QuickSearchLanguage

  return useQuery({
    queryKey: queryKeys.quickSearch(season, normalizedQuery, lang),
    queryFn: ({ signal }) => getQuickSearch({
      season,
      query: normalizedQuery,
      lang,
      signal,
    }),
    placeholderData: options.keepPreviousData && isValidQuickSearchQuery(normalizedQuery) ? keepPreviousData : undefined,
    enabled: isValidQuickSearchQuery(normalizedQuery) && options.enabled !== false,
    retry: false,
  })
}

export function useQuickSearchCategoryInfinite<C extends QuickSearchCategoryName>(
  query: string,
  season: string,
  category: C,
  options: Pick<UseQuickSearchOptions, 'enabled'> = {},
) {
  const normalizedQuery = query.trim()
  const lang = getCurrentLanguage().toUpperCase() as QuickSearchLanguage

  return useInfiniteScrollQuery<QuickSearchCategoryPage<C>, QuickSearchPageParam>({
    queryKey: queryKeys.quickSearchCategoryInfinite(season, normalizedQuery, category, lang),
    queryFn: ({ pageParam, signal }) => getQuickSearchCategory({
      season,
      query: normalizedQuery,
      lang,
      category,
      page: pageParam.page,
      pageSize: pageParam.pageSize,
      signal,
    }),
    initialPageParam: { page: 0, pageSize: QUICK_SEARCH_PAGE_SIZE },
    getNextPageParam: lastPage => lastPage.pageIndex + 1 < lastPage.totalPages
      ? { page: lastPage.pageIndex + 1, pageSize: lastPage.pageSize }
      : undefined,
    enabled: isValidQuickSearchQuery(normalizedQuery) && options.enabled !== false,
  })
}
